import type { SubscriptionDiscountInfo, StripeSubscription } from "../../../hooks/useStripeSubscriptions";

export interface SubscriptionItemPricing {
  subscriptionItemId: string | null;
  productName: string;
  listAmount: number;
  discountedAmount: number | null;
  currency: string;
}

export type DiscountExpiration = { type: "forever" } | { type: "expiring"; date: number } | { type: "expiring-soon" };

export interface SubscriptionPricingResult {
  currency: string;
  listTotal: number;
  discountedTotal: number | null;
  hasDiscount: boolean;
  discounts: SubscriptionDiscountInfo[];
  primaryDiscountLabel: string | null;
  additionalDiscountCount: number;
  discountExpiration: DiscountExpiration | null;
  itemBreakdown: SubscriptionItemPricing[];
  isPreviewAvailable: boolean;
  trialEndsAt: number | null;
  hasSchedule: boolean;
}

const getDiscountExpiration = (discounts: SubscriptionDiscountInfo[]): DiscountExpiration | null => {
  if (discounts.length === 0) return null;
  if (discounts.some((discount) => discount.duration === "forever")) return { type: "forever" };

  const soonestRepeatingEnd = discounts.reduce<number | null>((soonest, discount) => {
    if (discount.duration !== "repeating" || discount.end === null) return soonest;
    return soonest === null ? discount.end : Math.min(soonest, discount.end);
  }, null);
  if (soonestRepeatingEnd !== null) {
    return { type: "expiring", date: soonestRepeatingEnd };
  }

  if (discounts.some((discount) => discount.duration === "once")) return { type: "expiring-soon" };

  return null;
};

export const useSubscriptionPricing = (subscription: StripeSubscription): SubscriptionPricingResult => {
  const { effectivePlan, resolvedItems, pricePreview } = subscription;

  const listTotal = resolvedItems.reduce((sum, plan) => sum + plan.amount, 0);
  const currency = pricePreview?.currency ?? resolvedItems[0]?.currency ?? effectivePlan?.currency ?? "usd";

  const isPreviewAvailable = pricePreview !== null && pricePreview !== undefined;
  const discountedTotal = isPreviewAvailable ? pricePreview.amountDue : null;
  const discounts = isPreviewAvailable ? pricePreview.discounts : [];
  // Stripe can return totalDiscountAmount/amountDue correctly while discounts[] comes back
  // empty (e.g. an unexpanded discount reference) — still show the discounted total in that case,
  // just without a name/percentage label, rather than falling back to the undiscounted list price.
  const hasDiscount =
    isPreviewAvailable &&
    (discounts.length > 0 || pricePreview.totalDiscountAmount > 0) &&
    discountedTotal !== null &&
    discountedTotal < listTotal;

  const primaryDiscountLabel = discounts[0]?.name ?? null;
  const additionalDiscountCount = discounts.length > 1 ? discounts.length - 1 : 0;

  const discountExpiration = hasDiscount ? getDiscountExpiration(discounts) : null;

  const itemBreakdown: SubscriptionItemPricing[] =
    resolvedItems.length > 1
      ? resolvedItems.map((plan) => {
          const line = pricePreview?.lines.find((candidate) => candidate.subscriptionItemId === plan.id) ?? null;
          return {
            subscriptionItemId: plan.id,
            productName: typeof plan.product === "object" ? plan.product.name : "",
            listAmount: plan.amount,
            discountedAmount: line?.amount ?? null,
            currency: plan.currency,
          };
        })
      : [];

  return {
    currency,
    listTotal,
    discountedTotal,
    hasDiscount,
    discounts,
    primaryDiscountLabel,
    additionalDiscountCount,
    discountExpiration,
    itemBreakdown,
    isPreviewAvailable,
    trialEndsAt: subscription.status === "trialing" ? subscription.trial_end ?? null : null,
    hasSchedule: subscription.schedule !== null && subscription.schedule !== undefined,
  };
};
